import { ChangeEvent, useState } from "react";

import Select from "rivals/components/shared/Form/Select";
import TextAreaInput from "rivals/components/shared/Form/TextAreaInput";
import FormInput from "rivals/components/shared/Form/TextInput";
import { InputType } from "rivals/components/shared/Form/TextInput/types";
import SiteSelect from "rivals/components/shared/Subscription/SiteSelect";
import { setData } from "rivals/shared/utils/qa";
import styles from "./GiftPurchase.module.scss";
import strings from "./strings";
import { OwnProps } from "./types";
import Step from "../Step";

const {
  introNote,
  giftDetails,
  recipientName,
  recipientEmail,
  personalizedMessage,
  gifterName,
  gifterEmail,
  emailNote
} = strings;

function GiftPurchaseStep({
  formValues,
  giftPlans,
  onGiftPlanChange,
  personalNote,
  handleInputBlur,
  handleInputChange,
  handleSelectedSite,
  handlePersonalNoteBlur,
  handlePersonalNoteChange,
  handleSelectedSiteCleared,
  currentSite,
  selectedGiftPlan
}: OwnProps): JSX.Element {
  const MAX_CHARACTER_COUNT = 250;

  const introNoteWithName = introNote(currentSite.name);

  const philterPage = "gift-purchase-step";

  const siteSelector = (
    <div className={styles.input} {...setData(philterPage, "site-selector")}>
      <label
        className={styles.label}
        {...setData(philterPage, "site-selector-label")}
      >
        {strings.selectSite}
        <SiteSelect
          className={styles.siteSelect}
          onChange={handleSelectedSite}
          onClear={handleSelectedSiteCleared}
          placeholder={strings.selectSitePlaceholder}
        />
      </label>
    </div>
  );

  const [charsRemaining, setCharsRemaining] = useState<number>(
    MAX_CHARACTER_COUNT
  );

  const countCharsAndHandleChange = (
    e: ChangeEvent<HTMLTextAreaElement>
  ): void => {
    handlePersonalNoteChange(e);
    setCharsRemaining(MAX_CHARACTER_COUNT - e.target.value.length);
  };

  return (
    <div className={styles.giftPurchase}>
      <Step
        introNote={introNoteWithName}
        stepNumber={1}
        title={giftDetails}
        {...setData(philterPage, "intro-note")}
      >
        <>
          <div className={styles.label}>{strings.giftSubscriptionLength}</div>
          <Select<string>
            className={styles.input}
            data="gift-plan-select"
            defaultValue={selectedGiftPlan.displayName}
            onChange={onGiftPlanChange}
            options={giftPlans.map(giftPlan => ({
              name: giftPlan.displayName,
              value: giftPlan.displayName
            }))}
          />
          {!currentSite.subscribable && siteSelector}
          <FormInput
            className={styles.input}
            data="gifting-recipient-name-input"
            feedback={formValues.errors.recipientName}
            htmlFor="recipientName"
            label={recipientName}
            onBlur={handleInputBlur}
            onChange={handleInputChange}
            required={true}
            type={InputType.TEXT}
            value={formValues.recipientName}
          />
          <FormInput
            className={styles.input}
            data="gifting-recipient-email-input"
            feedback={formValues.errors.recipientEmail}
            htmlFor="recipientEmail"
            label={recipientEmail}
            onBlur={handleInputBlur}
            onChange={handleInputChange}
            required={true}
            type={InputType.TEXT}
            value={formValues.recipientEmail}
          />
          <TextAreaInput
            className={styles.input}
            data="gifting-personal-note"
            feedback={formValues.errors.personalNote}
            hint={`${charsRemaining} characters remaining`}
            htmlFor="personalNote"
            label={personalizedMessage}
            maxLength={MAX_CHARACTER_COUNT}
            onBlur={handlePersonalNoteBlur}
            onChange={countCharsAndHandleChange}
            required={true}
            value={personalNote}
          />
          <FormInput
            className={styles.input}
            data="gifting-gifter-name-input"
            feedback={formValues.errors.gifterName}
            htmlFor="gifterName"
            label={gifterName}
            onBlur={handleInputBlur}
            onChange={handleInputChange}
            required={true}
            type={InputType.TEXT}
            value={formValues.gifterName}
          />
          <FormInput
            className={styles.input}
            data="gifting-gifter-email-input"
            feedback={formValues.errors.gifterEmail}
            hint={emailNote}
            htmlFor="gifterEmail"
            label={gifterEmail}
            onBlur={handleInputBlur}
            onChange={handleInputChange}
            required={true}
            type={InputType.TEXT}
            value={formValues.gifterEmail}
          />
        </>
      </Step>
    </div>
  );
}

export default GiftPurchaseStep;
